import React, { useState, useEffect, useRef } from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import {
  Plus,
  ChevronRight,
  Layers,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
} from "lucide-react";
import { scenariosAPI } from "../services/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import AgentPicker from "../components/AgentPicker";
import StatusBadge, { STATUS_CONFIG } from "../components/ui/StatusBadge";
import StatCard from "../components/ui/StatCard";
import Pagination from "../components/ui/Pagination";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import { SearchInput } from "../components/ui/search-input";
import { FilterPopover } from "../components/ui/filter-popover";
import useSetPageTitle from "../hooks/useSetPageTitle";
import usePagination from "../hooks/usePagination";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { formatRelativeDate } from "../utils/date";
import { useCan } from "../hooks/useCan";

const BatchRun = () => {
  useSetPageTitle("Batch Runs");

  const navigate = useWsNavigate();
  const { user, currentWorkspace } = useAuth();
  const canWriteBatch = useCan("batch", "write");
  const { agents, selectedAgent, setSelectedAgent } = useAgent();
  const [batches, setBatches] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    completed: 0,
    running: 0,
    failed: 0,
    pending: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const { currentPage, totalPages, setCurrentPage, setTotalPages } =
    usePagination();

  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  // Ignore responses that a newer request has already superseded — search fires
  // a request per (debounced) keystroke, so they can land out of order.
  const requestId = useRef(0);

  useEffect(() => {
    if (user && currentWorkspace) fetchBatches(currentPage);
    // eslint-disable-next-line
  }, [
    user,
    currentWorkspace,
    currentPage,
    selectedAgent,
    debouncedSearch,
    filterStatus,
  ]);

  const fetchBatches = async (page = 1) => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      if (!currentWorkspace?.id) {
        setError("No workspace selected");
        setBatches([]);
        return;
      }
      const response = await scenariosAPI.getBatchesWithPagination(
        page,
        currentWorkspace.id,
        10,
        selectedAgent?.id,
        { search: debouncedSearch, status: filterStatus },
      );
      if (id !== requestId.current) return;
      if (response?.data) {
        setBatches(response.data.data || []);
        setCounts(
          response.data.counts || {
            total: 0,
            completed: 0,
            running: 0,
            failed: 0,
            pending: 0,
          },
        );
        setCurrentPage(response.data.pagination?.current_page || 1);
        setTotalPages(response.data.pagination?.total_pages || 1);
      } else {
        setBatches([]);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      setError("Failed to fetch batches");
      setBatches([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  // Search and status are applied server-side (they span every page), so the
  // rows we were handed are already the result set.
  const filteredBatches = batches;

  // Changing the query invalidates the page number — page 7 of the old result
  // set usually doesn't exist in the new one.
  const changeSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const changeStatus = (value) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  if (!user) return null;

  return (
    <div className="px-4">
      <PageHeader description="Run test scenarios against your AI agents and review results." />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Batches"
          value={counts.total}
          icon={<Layers className="h-5 w-5 text-muted-foreground" />}
          accent="bg-muted"
        />
        <StatCard
          label="Completed"
          value={counts.completed}
          icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />}
          accent="bg-emerald-50"
        />
        <StatCard
          label="Running"
          value={counts.running}
          icon={<Loader2 className="h-5 w-5 text-blue-600" />}
          accent="bg-blue-50"
        />
        <StatCard
          label="Failed"
          value={counts.failed}
          icon={<XCircle className="h-5 w-5 text-red-600" />}
          accent="bg-red-50"
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput
            placeholder="Search by name or agent..."
            value={searchTerm}
            onChange={(e) => changeSearch(e.target.value)}
            className="w-60"
          />

          {/* Filter popover */}
          <FilterPopover label="Filters" count={filterStatus !== "all" ? 1 : 0}>
            {({ close }) => (
              <>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Status
                </p>
                <div className="flex flex-col gap-0.5">
                  {[
                    { value: "all", label: "All statuses" },
                    {
                      value: "completed",
                      label: "Completed",
                      cfg: STATUS_CONFIG.completed,
                    },
                    {
                      value: "running",
                      label: "Running",
                      cfg: STATUS_CONFIG.running,
                    },
                    {
                      value: "failed",
                      label: "Failed",
                      cfg: STATUS_CONFIG.failed,
                    },
                    {
                      value: "pending",
                      label: "Pending",
                      cfg: STATUS_CONFIG.pending,
                    },
                  ].map(({ value, label, cfg }) => (
                    <button
                      key={value}
                      onClick={() => {
                        changeStatus(value);
                        close();
                      }}
                      className={`flex items-center justify-between w-full px-2.5 py-2 rounded-lg text-sm transition-colors ${
                        filterStatus === value
                          ? "bg-muted"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      {cfg ? (
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}
                          />
                          {label}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {label}
                        </span>
                      )}
                      {filterStatus === value && (
                        <svg
                          className="h-4 w-4 text-indigo-600 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
                {filterStatus !== "all" && (
                  <button
                    onClick={() => {
                      changeStatus("all");
                      close();
                    }}
                    className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground py-1.5 rounded-lg hover:bg-muted/50 transition-colors border-t border-border pt-3"
                  >
                    Clear filter
                  </button>
                )}
              </>
            )}
          </FilterPopover>

          {/* Active filter chip */}
          {filterStatus !== "all" &&
            (() => {
              const cfg = STATUS_CONFIG[filterStatus];
              return (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}
                >
                  {cfg.label}
                  <button
                    onClick={() => changeStatus("all")}
                    className="ml-0.5 opacity-70 hover:opacity-100"
                  >
                    ×
                  </button>
                </span>
              );
            })()}
        </div>

        <div className="flex items-center gap-3">
          {agents.length > 0 && (
            <AgentPicker
              agents={agents}
              selectedAgent={selectedAgent}
              setSelectedAgent={setSelectedAgent}
              onAdd={() => navigate("/ai-testing-agent/create")}
            />
          )}
          {canWriteBatch && (
            <button
              onClick={() => navigate("/batch-run/new")}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Batch
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-16 text-center">
          <div className="mx-auto w-14 h-14 bg-muted rounded-full flex items-center justify-center mb-4">
            <Layers className="h-7 w-7 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">
            No batch runs found
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            {searchTerm || filterStatus !== "all"
              ? "No batches match your filters"
              : "Run your first batch to start testing your AI agent"}
          </p>
          {canWriteBatch && (
            <button
              onClick={() => navigate("/batch-run/new")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Batch Run
            </button>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <Table className="min-w-max">
            <TableHeader>
              <TableRow>
                <TableHead>Batch Name</TableHead>
                <TableHead>Test. Agent</TableHead>
                <TableHead className="w-24">Scenarios</TableHead>
                <TableHead className="w-36">Progress</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-32">Created</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBatches.map((batch) => {
                const doneCount =
                  (batch.completed_count || 0) + (batch.failed_count || 0);
                const total = batch.run_count || 0;
                return (
                  <TableRow
                    key={batch.id}
                    className="cursor-pointer group"
                    onClick={() => navigate(`/batch-run/${batch.id}`)}
                  >
                    <TableCell>
                      <span
                        className="block max-w-[260px] truncate text-sm font-semibold text-foreground group-hover:text-primary transition-colors"
                        title={batch.name}
                      >
                        {batch.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[180px] truncate text-sm text-muted-foreground"
                        title={batch.agent_name || ""}
                      >
                        {batch.agent_name || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {total}
                      </span>
                    </TableCell>
                    <TableCell>
                      {total > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                batch.failed_count > 0
                                  ? "bg-red-400"
                                  : "bg-emerald-400"
                              }`}
                              style={{ width: `${(doneCount / total) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {doneCount}/{total}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={batch.status} />
                    </TableCell>
                    <TableCell>
                      <div
                        className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap"
                        title={
                          batch.created_at
                            ? new Date(batch.created_at).toLocaleString()
                            : ""
                        }
                      >
                        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">
                          {formatRelativeDate(batch.created_at)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default BatchRun;
